import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { liveChannelFor, subscribeLiveState } from "../../utils/liveFirebase";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./leaderboard.css";
import { fetchReport } from "../../functions/fetchReport";
import { getGameContent } from "../../functions/crossroadsApi";
import { resolveCrossroadsTheme, themeCssVars } from "../../utils/crossroadsTheme";
import { avatarFor } from "../../components/crossroads/JourneyScene";
import { useLogoBacking } from "../../utils/useHeaderOffset";

function timeToMss(raw) {
  if (!raw) return "00:00";
  const s = String(raw).trim();
  if (!s) return "00:00";
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.some((x) => !Number.isFinite(x))) return "00:00";
  let total = 0;
  if (parts.length === 3) total = parts[0] * 3600 + parts[1] * 60 + parts[2];
  else if (parts.length === 2) total = parts[0] * 60 + parts[1];
  else total = parts[0];
  total = Math.max(0, Math.floor(total));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

const TOP_N = 5;

/**
 * After the game: podium for the top three, ranked list below, the player's own row highlighted.
 * If the admin switched the leaderboard off, only the player's own result (+ admin message).
 */
function Leaderboard() {
  useLogoBacking();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus, currentTheme } = useSelector((state) => state.theme);
  const authUser = useSelector((state) => state.auth?.user);

  const [top, setTop] = useState([]);
  const [me, setMe] = useState(null);
  const [meReportFallback, setMeReportFallback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState(undefined); // undefined = loading, null = unavailable

  // Published game settings decide whether there is a leaderboard at all.
  useEffect(() => {
    if (!adminToken) {
      setContent(null);
      return undefined;
    }
    let cancelled = false;
    getGameContent({ token: adminToken })
      .then((c) => !cancelled && setContent(c))
      .catch(() => !cancelled && setContent(null));
    return () => {
      cancelled = true;
    };
  }, [adminToken]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  // Soft blur on the board art behind the panel.
  useEffect(() => {
    const body = document.body;
    const prev = body.style.getPropertyValue("backdrop-filter");
    body.style.setProperty("backdrop-filter", "blur(9px)");
    return () => {
      if (prev) body.style.setProperty("backdrop-filter", prev);
      else body.style.removeProperty("backdrop-filter");
    };
  }, []);

  useEffect(() => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/getLeaderboard`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: authUser?.userId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success) {
          setTop(Array.isArray(data.top) ? data.top : []);
          setMe(data.me || null);
        } else {
          setError(data.error || data.message || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, authUser?.userId]);

  useEffect(() => {
    if (!adminToken || !authUser?.userId) return;
    if (me || (Array.isArray(top) && top.some((r) => String(r.userId) === String(authUser.userId)))) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchReport({ userId: authUser.userId, token: adminToken, gameId: authUser?.gameId || null });
        if (cancelled) return;
        setMeReportFallback(resp?.report || null);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, authUser?.userId, authUser?.gameId, me, top]);

  const rows = useMemo(() => {
    // Top 5, then the player's own row (with their real rank) when they're not among them.
    const out = (Array.isArray(top) ? top : []).slice(0, TOP_N);
    if (me && !out.some((r) => String(r.userId) === String(me.userId))) out.push(me);
    if (!me && out.length === 0 && meReportFallback && authUser?.userId) {
      out.push({
        rank: "—",
        userId: authUser.userId,
        name: authUser?.name || authUser?.email || "You",
        points: meReportFallback.total_score ?? meReportFallback.points ?? 0,
        time: meReportFallback.time ?? "00:00",
      });
    }
    return out;
  }, [top, me, meReportFallback, authUser?.userId, authUser?.name, authUser?.email]);
  const meUserId = String(authUser?.userId ?? "");

  const game = useMemo(() => resolveCrossroadsTheme(content?.settings), [content]);

  // Live games: when the host starts a new game (a new run, or the game isn't finished any
  // more), players waiting here go straight back to the game screen to play it.
  const finishedRunRef = useRef(undefined);
  const liveChannel = game.mode === "live" && authUser ? liveChannelFor(authUser.organizationId, authUser.sessionId) : null;
  useEffect(() => {
    if (!liveChannel) return undefined;
    return subscribeLiveState(liveChannel, (state) => {
      if (!state?.runId) return;
      if (finishedRunRef.current === undefined) finishedRunRef.current = state.phase === "complete" ? state.runId : null;
      const newGame = state.runId !== finishedRunRef.current || state.phase !== "complete";
      if (newGame) navigate("/vote", { replace: true });
    });
  }, [liveChannel, navigate]);
  const nameOf = (row) => String(row.name ?? row.fullName ?? row.username ?? row.email ?? "").trim() || "Player";
  const isMe = (row) => meUserId && String(row.userId) === meUserId;

  // Leaderboard switched off by the admin: show only this player's own result.
  if (content && !game.leaderboardEnabled) {
    const mine =
      me ||
      (Array.isArray(top) ? top.find((r) => String(r.userId) === meUserId) : null) ||
      (meReportFallback
        ? { points: meReportFallback.total_score ?? meReportFallback.points ?? 0, time: meReportFallback.time }
        : null);
    const maxScore = (content.questions || []).reduce(
      (s, q) => s + (q.question_type === "choice" ? 0 : Math.max(0, Number(q.points) || 0)),
      0
    );
    const time = mine?.time ? timeToMss(mine.time) : "";
    const showTime = game.showCompletionTime && time && time !== "00:00";
    return (
      <div className="lb-page" style={themeCssVars(game)}>
        <div className="lb-panel lb-panel--result">
          <header className="lb-head">
            <span className="lb-head-icon" aria-hidden>
              🏁
            </span>
            <h1 className="lb-title">Your Result</h1>
          </header>
          {loading ? (
            <p className="lb-status">Loading your result…</p>
          ) : !mine ? (
            <p className="lb-status">Finish the journey to see your result.</p>
          ) : (
            <div className="lb-result">
              {game.hasCorrectAnswers ? (
                <div className="lb-result-stat">
                  <span className="lb-result-label">Your score</span>
                  <strong className="lb-result-value">
                    {Number(mine.points ?? 0) || 0}
                    <small> / {maxScore}</small>
                  </strong>
                </div>
              ) : (
                <div className="lb-result-stat">
                  <span className="lb-result-label">Journey</span>
                  <strong className="lb-result-value">Completed</strong>
                </div>
              )}
              {showTime ? (
                <div className="lb-result-stat">
                  <span className="lb-result-label">Time taken</span>
                  <strong className="lb-result-value">{time}</strong>
                </div>
              ) : null}
            </div>
          )}
          {game.leaderboardMessage ? <p className="lb-message">{game.leaderboardMessage}</p> : null}
        </div>
      </div>
    );
  }

  const ranked = rows.map((row, idx) => ({
    ...row,
    sr: Number.isFinite(Number(row.rank)) && Number(row.rank) > 0 ? Number(row.rank) : idx + 1,
  }));
  const podium = ranked.filter((r) => r.sr <= 3).slice(0, 3);
  const rest = ranked.filter((r) => !podium.includes(r));
  // Podium order on screen: 2nd, 1st, 3rd.
  const podiumOrder = [podium.find((r) => r.sr === 2), podium.find((r) => r.sr === 1), podium.find((r) => r.sr === 3)].filter(Boolean);
  const busy = loading || content === undefined || (themeStatus === "loading" && !themeData);

  return (
    <div className="lb-page" style={themeCssVars(game)}>
      <div className="lb-panel">
        <header className="lb-head">
          <span className="lb-head-icon" aria-hidden>
            🏆
          </span>
          <h1 className="lb-title">Leaderboard</h1>
          <p className="lb-sub">Top travellers of the journey</p>
        </header>

        {busy ? (
          <p className="lb-status">Loading scores…</p>
        ) : error ? (
          <p className="lb-status lb-status--error">{error}</p>
        ) : ranked.length === 0 ? (
          <p className="lb-status">No results yet — be the first to finish the journey!</p>
        ) : (
          <>
            <ol className="lb-podium" aria-label="Top three">
              {podiumOrder.map((row) => {
                const a = avatarFor(row.userId, nameOf(row));
                return (
                  <li key={`p-${row.userId}`} className={`lb-podium-spot lb-podium-spot--${row.sr}${isMe(row) ? " is-me" : ""}`}>
                    {row.sr === 1 ? (
                      <span className="lb-crown" aria-hidden>
                        👑
                      </span>
                    ) : null}
                    <span className="lb-podium-avatar" style={{ borderColor: a.ring }} aria-hidden>
                      {a.emoji}
                    </span>
                    <span className="lb-podium-name" title={nameOf(row)}>
                      {nameOf(row)}
                      {isMe(row) ? <em> (you)</em> : null}
                    </span>
                    <span className="lb-podium-points">{Number(row.points ?? 0) || 0} pts</span>
                    <span className="lb-podium-time">⏱ {timeToMss(row.time)}</span>
                    <span className="lb-podium-block">
                      <span className="lb-podium-rank">{row.sr}</span>
                    </span>
                  </li>
                );
              })}
            </ol>

            {rest.length ? (
              <ol className="lb-list">
                <li className="lb-list-head" aria-hidden>
                  <span>#</span>
                  <span>Player</span>
                  <span>Points</span>
                  <span>Time</span>
                </li>
                {rest.map((row) => {
                  const a = avatarFor(row.userId, nameOf(row));
                  return (
                    <li key={`r-${row.userId}-${row.sr}`} className={`lb-row${isMe(row) ? " is-me" : ""}`}>
                      <span className="lb-row-rank">{row.sr}</span>
                      <span className="lb-row-player">
                        <span className="lb-row-avatar" style={{ borderColor: a.ring }} aria-hidden>
                          {a.emoji}
                        </span>
                        <span className="lb-row-name" title={nameOf(row)}>
                          {nameOf(row)}
                          {isMe(row) ? <em> (you)</em> : null}
                        </span>
                      </span>
                      <span className="lb-row-points">{Number(row.points ?? 0) || 0}</span>
                      <span className="lb-row-time">{timeToMss(row.time)}</span>
                    </li>
                  );
                })}
              </ol>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
